import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../utils/auth';

const API = '/api';

/**
 * Custom hook for case submission and package handling
 */
export const useChatSubmit = ({
  messages,
  clientType,
  caseCategory,
  caseSubCategory,
  caseSummary,
  isExistingCustomer,
  attachments,
  addMessage,
  setIsTyping,
  setShowInput,
  setIsInReviewMode
}) => {
  const navigate = useNavigate();
  
  // Package state
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPackageSelector, setShowPackageSelector] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  
  // AI Triage state
  const [triageResult, setTriageResult] = useState(null);
  const [showEscalationModal, setShowEscalationModal] = useState(false);
  
  // Auth state
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [authMode, setAuthMode] = useState('register');
  const [authForm, setAuthForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [isSubmittingAuth, setIsSubmittingAuth] = useState(false);

  // Use refs to avoid circular dependency issues
  const submitCaseRef = useRef(null);
  const proceedToAuthRef = useRef(null);
  const runAITriageRef = useRef(null);

  // Submit case
  const submitCase = useCallback(async () => {
    setIsTyping(true);
    try {
      const token = auth.getToken();
      const caseData = {
        title: `${caseCategory} - ${clientType === 'PERSON' ? 'Privatperson' : 'Bedrift'}`,
        description: messages.filter(m => m.role === 'user' && !m.content.includes('\ud83d\udcdd') && !m.content.includes('\u2705') && !m.content.includes('\ud83d\udcce') && !m.content.includes('\ud83d\udce6') && !m.content.includes('\u27a1\ufe0f')).map(m => m.content).join('\n\n'),
        client_type: clientType,
        category: caseCategory,
        subcategory: caseSubCategory,
        status: 'AWAITING_LAWYER_REVIEW',
        uploaded_documents: [],
        pending_document_review: attachments.length > 0,
        ai_summary: caseSummary,
        is_existing_customer: isExistingCustomer === true,
        package_level: selectedPackage?.id,
        package_price: selectedPackage?.price,
        package_name: selectedPackage?.name,
        package_docs_limit: selectedPackage?.docs || 0,
        source: 'AFA'
      };
      
      const response = await axios.post(`${API}/cases`, caseData, { headers: { Authorization: `Bearer ${token}` } });
      const caseId = response.data.id;
      
      // Upload attachments
      for (const attachment of attachments) {
        try {
          const formData = new FormData();
          formData.append('file', attachment.file);
          await axios.post(`${API}/documents/upload?case_id=${caseId}`, formData, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
          });
        } catch (uploadError) {
          console.error('File upload error:', uploadError);
        }
      }
      
      setIsTyping(false);
      addMessage('assistant', '\ud83c\udf89 **Saken er sendt inn!**\n\nAdvokater kan n\u00e5 se saken din og ta kontakt. Du vil motta en e-post n\u00e5r en advokat tar saken.\n\nTakk for at du bruker **Advokat for alle**!');
      toast.success('Saken er sendt inn!');
      setTimeout(() => navigate(`/case/${response.data.id}`), 3000);
    } catch (error) {
      console.error('Submit error:', error);
      setIsTyping(false);
      addMessage('assistant', '\u26a0\ufe0f Kunne ikke sende inn saken. Pr\u00f8v igjen.');
      toast.error('Kunne ikke sende inn saken');
    }
  }, [messages, clientType, caseCategory, caseSubCategory, caseSummary, isExistingCustomer, selectedPackage, attachments, addMessage, setIsTyping, navigate]);

  // Proceed to authentication
  const proceedToAuth = useCallback(() => {
    if (!auth.isAuthenticated()) {
      addMessage('assistant', 'For \u00e5 sende inn saken trenger vi bare noen f\u00e5 opplysninger:');
      setShowAuthForm(true);
      setAuthMode('register');
    } else {
      submitCaseRef.current?.();
    }
  }, [addMessage]);

  // Run AI triage
  const runAITriage = useCallback(async () => {
    setIsTyping(true);
    addMessage('assistant', '\ud83e\udd16 AI analyserer kompleksiteten i saken din...');
    
    try {
      const userMessages = messages
        .filter(m => m.role === 'user' && !m.content.includes('\ud83d\udcdd') && !m.content.includes('\u2705') && !m.content.includes('\ud83d\udcce') && !m.content.includes('\ud83d\udce6') && !m.content.includes('\u27a1\ufe0f'))
        .map(m => m.content).join('\n\n');
      
      const response = await axios.post(`${API}/afa/assess-complexity`, {
        case_description: userMessages,
        case_type: caseCategory || 'Generell',
        subcase_type: caseSubCategory || '',
        package_level: selectedPackage?.id,
        num_attachments: attachments.length,
        total_pages: 0
      });
      
      setIsTyping(false);
      setTriageResult(response.data);
      
      if (response.data.fit === 'OVER_THRESHOLD') {
        addMessage('assistant', '\u26a0\ufe0f AI har identifisert at saken kan v\u00e6re mer kompleks enn valgt pakke.');
        setShowEscalationModal(true);
      } else {
        addMessage('assistant', '\u2705 Saken passer godt innenfor valgt pakke!');
        setTimeout(() => proceedToAuthRef.current?.(), 1000);
      }
    } catch (error) {
      console.error('Triage error:', error);
      setIsTyping(false);
      addMessage('assistant', 'Fortsetter med innsending...');
      setTimeout(() => proceedToAuthRef.current?.(), 500);
    }
  }, [messages, caseCategory, caseSubCategory, selectedPackage, attachments, addMessage, setIsTyping]);

  // Store refs in useEffect to avoid render-time ref updates
  useEffect(() => {
    submitCaseRef.current = submitCase;
  }, [submitCase]);

  useEffect(() => {
    proceedToAuthRef.current = proceedToAuth;
  }, [proceedToAuth]);

  useEffect(() => {
    runAITriageRef.current = runAITriage;
  }, [runAITriage]);

  // Handle review actions (ADD_MORE, UPLOAD_DOCS, CONTINUE)
  const handleReviewAction = useCallback(async (action) => {
    if (action === 'ADD_MORE') {
      addMessage('user', '\ud83d\udcdd Jeg vil legge til mer informasjon');
      setIsInReviewMode(false);
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('assistant', 'Perfekt! Legg gjerne til mer informasjon om situasjonen din. \ud83d\udcac');
        setShowInput(true);
      }, 800);
    } else if (action === 'UPLOAD_DOCS') {
      addMessage('user', '\ud83d\udcce Last opp vedlegg');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('assistant', '\ud83d\udce6 **Velg pakke med dokumentgjennomgang**\n\nFor \u00e5 laste opp vedlegg m\u00e5 du velge Dokument-pakken (1 vedlegg) eller Utvidet-pakken (opptil 5 vedlegg).');
        setShowPackageSelector(true);
      }, 800);
    } else if (action === 'CONTINUE') {
      addMessage('user', '\u27a1\ufe0f G\u00e5 videre');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('assistant', '\ud83d\udce6 **Velg pakke for din juridiske vurdering**\n\nVelg pakken som passer best for din situasjon. Alle pakker har fastpris og leveres av godkjent advokat.');
        setShowPackageSelector(true);
      }, 800);
    }
  }, [addMessage, setIsTyping, setShowInput, setIsInReviewMode]);

  // Handle package selection
  const handlePackageSelected = useCallback((pkg) => {
    setSelectedPackage(pkg);
    setShowPackageSelector(false);
    addMessage('user', `\ud83d\udce6 ${pkg.name}-pakken (${pkg.price.toLocaleString('nb-NO')} kr)`);
    
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      if (pkg.docs > 0) {
        addMessage('assistant', `\u2705 **Perfekt! Du har valgt ${pkg.name}-pakken.**\n\nDu kan n\u00e5 laste opp ${pkg.docs === 1 ? '1 dokument' : `opptil ${pkg.docs} dokumenter`} som advokaten skal gjennomg\u00e5 sammen med din beskrivelse.`);
        setShowFileUpload(true);
      } else {
        addMessage('assistant', '\u2705 **Perfekt! Du har valgt Basis-pakken.**\n\nDenne pakken inkluderer ikke dokumentgjennomgang, men advokaten vil gi deg en vurdering basert p\u00e5 din beskrivelse.\n\nKlar til \u00e5 sende inn?');
        setTimeout(() => proceedToAuthRef.current?.(), 1500);
      }
    }, 800);
  }, [addMessage, setIsTyping]);

  // Handle file upload continue
  const handleFileUploadContinue = useCallback(async (currentAttachments) => {
    setShowFileUpload(false);
    if (currentAttachments.length > 0) {
      addMessage('user', `\ud83d\udcce ${currentAttachments.length} dokument${currentAttachments.length > 1 ? 'er' : ''} lastet opp`);
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('assistant', `Flott! Jeg har mottatt ${currentAttachments.length} dokument${currentAttachments.length > 1 ? 'er' : ''}. Advokaten vil gjennomg\u00e5 ${currentAttachments.length > 1 ? 'disse' : 'dette'} sammen med din beskrivelse.`);
        runAITriageRef.current?.();
      }, 500);
    } else {
      addMessage('user', 'Fortsett uten dokumenter');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('assistant', 'Ok, vi fortsetter uten dokumenter.');
        runAITriageRef.current?.();
      }, 500);
    }
  }, [addMessage, setIsTyping]);

  // Handle escalation modal actions
  const handleContinueWithPackage = useCallback(() => {
    setShowEscalationModal(false);
    addMessage('user', 'Fortsett med valgt pakke');
    addMessage('assistant', 'Ok! Vi fortsetter med valgt pakke. Advokaten vil gj\u00f8re sitt beste innenfor pakke-rammene.');
    setTimeout(() => proceedToAuthRef.current?.(), 1000);
  }, [addMessage]);

  const handleUpgradeToFullCase = useCallback(() => {
    setShowEscalationModal(false);
    addMessage('user', 'Jeg vil ha tilpasset tilbud');
    addMessage('assistant', '\ud83d\udc4d Perfekt! Din sak vil bli publisert som en tilbudssak. Advokater kan se saken og gi deg skreddersydde tilbud basert p\u00e5 faktisk omfang.');
    setTimeout(() => proceedToAuthRef.current?.(), 1500);
  }, [addMessage]);

  // Handle auth submit
  const handleAuthSubmit = useCallback(async (e) => {
    e.preventDefault();
    setAuthError('');
    setIsSubmittingAuth(true);
    
    try {
      if (authMode === 'register') {
        await auth.register(authForm.name, authForm.email, authForm.phone, authForm.password);
        toast.success('Konto opprettet!');
      } else {
        await auth.login(authForm.email, authForm.password);
        toast.success('Logget inn!');
      }
      setShowAuthForm(false);
      await submitCaseRef.current?.();
    } catch (error) {
      console.error('Auth error:', error);
      setAuthError(error.response?.data?.detail || 'Noe gikk galt. Pr\u00f8v igjen.');
      setIsSubmittingAuth(false);
    }
  }, [authMode, authForm]);

  return {
    // Package state
    selectedPackage,
    showPackageSelector,
    setShowPackageSelector,
    showFileUpload,
    setShowFileUpload,
    
    // Triage state
    triageResult,
    showEscalationModal,
    setShowEscalationModal,
    
    // Auth state
    showAuthForm,
    setShowAuthForm,
    authMode,
    setAuthMode,
    authForm,
    setAuthForm,
    authError,
    setAuthError,
    isSubmittingAuth,
    
    // Actions
    handleReviewAction,
    handlePackageSelected,
    handleFileUploadContinue,
    handleContinueWithPackage,
    handleUpgradeToFullCase,
    handleAuthSubmit,
    submitCase
  };
};

export default useChatSubmit;
